/*
  COOKIE CONSENT BANNER
  ----------------------------------------------------------------------------------------------------------------------
  Hand-injected replacement for the never-deployed cookie-consent-hub Shopify app (see project memory
  cookie_consent_hub.md). No Shopify app, no OAuth, no Admin API token: consent is recorded entirely through
  Shopify's own client-side Customer Privacy API (window.Shopify.customerPrivacy), which is what actually gates
  whether Shopify's own pixel/CAPI integrations (Meta, Google, TikTok...) fire — nothing here talks to those
  platforms directly.

  Ported and reviewed from cookie-consent-hub's extension code (consent-state.js + consent-banner.js +
  banner-ui.js), merged into one plain script (no ES module imports — those don't resolve reliably against
  Shopify's asset CDN URLs). Two real fixes made during this port, both verified against Shopify's official
  Customer Privacy API docs and the first-party TypeScript types in Shopify's Hydrogen repo:

  1. shouldShowBanner() gate added (was completely missing before). Without it the banner showed to every
     visitor worldwide regardless of region. That's both a UX mismatch (opt-in banners are only legally
     required in consent-regulated regions) and directly counterproductive to maximizing matched conversions:
     in non-regulated regions Shopify's own region defaults already allow tracking, so forcing an extra click
     before any pixel can fire there was pure event loss for no compliance benefit. If shouldShowBanner() is
     unavailable or throws (older runtime), we fail safe by SHOWING the banner rather than skipping it.
  2. For non-regulated visitors (shouldShowBanner() === false) we render nothing and call nothing — we do NOT
     auto-fire setTrackingConsent. Shopify's own regional defaults already apply; explicit consent calls are
     only for actual visitor interaction.

  Category -> Shopify consent field mapping (unchanged from the reviewed original, verified correct against
  Shopify's documented contract):
    performance -> analytics
    functional  -> preferences
    targeting   -> marketing AND sale_of_data (CCPA "don't sell/share" opt-out lives under the same toggle)
    necessary   -> always on, never sent (Shopify has no field for it — it's not a tracking category)
*/
(function () {
  'use strict';

  var TRANSLATIONS = {
    en: {
      step1: {
        title: 'Cookies',
        body: 'By clicking "Accept", you agree to the storing of cookies on your device to enhance site navigation, analyse site usage and assist in our marketing efforts.',
        acceptAll: 'Accept All',
        chooseLink: 'Preferences',
      },
      step2: {
        title: 'Privacy Preferences',
        body: 'When you visit this site, it may store or retrieve information in your browser, mostly in the form of cookies. You can choose not to allow some types of cookies below.',
        allowAll: 'Allow',
        confirm: 'Confirm My Choices',
        errors: { saveFailed: 'Something went wrong saving your choice. Please try again.' },
        categories: {
          necessary: { name: 'Strictly Necessary', desc: 'Required for the site to function (cart, checkout, security). Cannot be switched off.', alwaysActive: 'Always active' },
          performance: { name: 'Performance', desc: 'Let us count visits and traffic sources to measure and improve site performance.' },
          functional: { name: 'Functional', desc: 'Enable enhanced functionality and personalisation.' },
          targeting: { name: 'Targeting', desc: 'Used by our advertising partners to show relevant ads on other sites. Includes not selling or sharing your personal information for advertising purposes (CCPA).' },
        },
      },
    },
    es: {
      step1: {
        title: 'Cookies',
        body: 'Al hacer clic en "Aceptar todas", aceptas el almacenamiento de cookies en tu dispositivo para mejorar la navegación, analizar el uso del sitio y ayudarnos en nuestro marketing.',
        acceptAll: 'Aceptar todas',
        chooseLink: 'Preferencias',
      },
      step2: {
        title: 'Preferencias de privacidad',
        body: 'Cuando visitas este sitio, puede almacenar o recuperar información en tu navegador, normalmente en forma de cookies. Puedes elegir no permitir algunos tipos a continuación.',
        allowAll: 'Permitir',
        confirm: 'Confirmar mi elección',
        errors: { saveFailed: 'Algo salió mal al guardar tu elección. Inténtalo de nuevo.' },
        categories: {
          necessary: { name: 'Estrictamente necesarias', desc: 'Imprescindibles para que el sitio funcione (carrito, pago, seguridad). No se pueden desactivar.', alwaysActive: 'Siempre activas' },
          performance: { name: 'Rendimiento', desc: 'Nos permiten contar visitas y fuentes de tráfico para medir y mejorar el rendimiento del sitio.' },
          functional: { name: 'Funcionales', desc: 'Habilitan funciones mejoradas y personalización.' },
          targeting: { name: 'Publicidad', desc: 'Nuestros socios publicitarios las usan para mostrarte anuncios relevantes en otros sitios. Incluye no vender ni compartir tu información personal con fines publicitarios (CCPA).' },
        },
      },
    },
    'pt-BR': {
      step1: {
        title: 'Cookies',
        body: 'Ao clicar em "Aceitar todos", você concorda com o armazenamento de cookies no seu dispositivo para melhorar a navegação, analisar o uso do site e apoiar nossas ações de marketing.',
        acceptAll: 'Aceitar todos',
        chooseLink: 'Preferências',
      },
      step2: {
        title: 'Preferências de privacidade',
        body: 'Ao visitar este site, ele pode armazenar ou recuperar informações no seu navegador, geralmente na forma de cookies. Você pode optar por não permitir alguns tipos abaixo.',
        allowAll: 'Permitir',
        confirm: 'Confirmar minha escolha',
        errors: { saveFailed: 'Algo deu errado ao salvar sua escolha. Tente novamente.' },
        categories: {
          necessary: { name: 'Estritamente necessários', desc: 'Essenciais para o funcionamento do site (carrinho, pagamento, segurança). Não podem ser desativados.', alwaysActive: 'Sempre ativos' },
          performance: { name: 'Desempenho', desc: 'Permitem contar visitas e origens de tráfego para medir e melhorar o desempenho do site.' },
          functional: { name: 'Funcionais', desc: 'Habilitam funcionalidades avançadas e personalização.' },
          targeting: { name: 'Publicidade', desc: 'Usados por nossos parceiros de publicidade para exibir anúncios relevantes em outros sites. Inclui não vender ou compartilhar suas informações pessoais para fins de publicidade (CCPA).' },
        },
      },
    },
    'pt-PT': {
      step1: {
        title: 'Cookies',
        body: 'Ao clicar em "Aceitar todos", concorda com o armazenamento de cookies no seu dispositivo para melhorar a navegação, analisar a utilização do site e apoiar as nossas ações de marketing.',
        acceptAll: 'Aceitar todos',
        chooseLink: 'Preferências',
      },
      step2: {
        title: 'Preferências de privacidade',
        body: 'Ao visitar este site, este pode armazenar ou recuperar informações no seu navegador, normalmente sob a forma de cookies. Pode optar por não permitir alguns tipos abaixo.',
        allowAll: 'Permitir',
        confirm: 'Confirmar a minha escolha',
        errors: { saveFailed: 'Ocorreu um erro ao guardar a sua escolha. Tente novamente.' },
        categories: {
          necessary: { name: 'Estritamente necessários', desc: 'Essenciais para o funcionamento do site (carrinho, pagamento, segurança). Não podem ser desativados.', alwaysActive: 'Sempre ativos' },
          performance: { name: 'Desempenho', desc: 'Permitem contar visitas e origens de tráfego para medir e melhorar o desempenho do site.' },
          functional: { name: 'Funcionais', desc: 'Ativam funcionalidades avançadas e personalização.' },
          targeting: { name: 'Publicidade', desc: 'Utilizados pelos nossos parceiros publicitários para mostrar anúncios relevantes noutros sites. Inclui não vender nem partilhar os seus dados pessoais para fins publicitários (CCPA).' },
        },
      },
    },
    fr: {
      step1: {
        title: 'Cookies',
        body: 'En cliquant sur "Tout accepter", vous acceptez le stockage de cookies sur votre appareil afin d’améliorer la navigation, d’analyser l’utilisation du site et de soutenir nos actions marketing.',
        acceptAll: 'Tout accepter',
        chooseLink: 'Préférences',
      },
      step2: {
        title: 'Préférences de confidentialité',
        body: 'Lorsque vous visitez ce site, il peut stocker ou récupérer des informations dans votre navigateur, généralement sous forme de cookies. Vous pouvez refuser certains types ci-dessous.',
        allowAll: 'Autoriser',
        confirm: 'Confirmer mon choix',
        errors: { saveFailed: 'Une erreur est survenue lors de l’enregistrement de votre choix. Veuillez réessayer.' },
        categories: {
          necessary: { name: 'Strictement nécessaires', desc: 'Indispensables au fonctionnement du site (panier, paiement, sécurité). Ne peuvent pas être désactivés.', alwaysActive: 'Toujours actifs' },
          performance: { name: 'Performance', desc: 'Nous permettent de compter les visites et les sources de trafic pour mesurer et améliorer les performances du site.' },
          functional: { name: 'Fonctionnels', desc: 'Activent des fonctionnalités avancées et la personnalisation.' },
          targeting: { name: 'Publicité', desc: 'Utilisés par nos partenaires publicitaires pour vous montrer des publicités pertinentes sur d’autres sites. Inclut le fait de ne pas vendre ni partager vos données personnelles à des fins publicitaires (CCPA).' },
        },
      },
    },
    it: {
      step1: {
        title: 'Cookie',
        body: 'Cliccando su "Accetta tutti", acconsenti alla memorizzazione di cookie sul tuo dispositivo per migliorare la navigazione, analizzare l’uso del sito e supportare le nostre attività di marketing.',
        acceptAll: 'Accetta tutti',
        chooseLink: 'Preferenze',
      },
      step2: {
        title: 'Preferenze sulla privacy',
        body: 'Quando visiti questo sito, potrebbe memorizzare o recuperare informazioni nel tuo browser, solitamente sotto forma di cookie. Puoi scegliere di non consentire alcuni tipi qui sotto.',
        allowAll: 'Consenti',
        confirm: 'Conferma le mie scelte',
        errors: { saveFailed: 'Si è verificato un errore durante il salvataggio della scelta. Riprova.' },
        categories: {
          necessary: { name: 'Strettamente necessari', desc: 'Indispensabili per il funzionamento del sito (carrello, pagamento, sicurezza). Non possono essere disattivati.', alwaysActive: 'Sempre attivi' },
          performance: { name: 'Prestazioni', desc: 'Ci permettono di contare visite e fonti di traffico per misurare e migliorare le prestazioni del sito.' },
          functional: { name: 'Funzionali', desc: 'Abilitano funzionalità avanzate e personalizzazione.' },
          targeting: { name: 'Pubblicità', desc: 'Usati dai nostri partner pubblicitari per mostrarti annunci pertinenti su altri siti. Include il non vendere né condividere i tuoi dati personali per finalità pubblicitarie (CCPA).' },
        },
      },
    },
    de: {
      step1: {
        title: 'Cookies',
        body: 'Wenn Sie auf "Alle akzeptieren" klicken, stimmen Sie der Speicherung von Cookies auf Ihrem Gerät zu, um die Navigation zu verbessern, die Nutzung der Website zu analysieren und unsere Marketingmaßnahmen zu unterstützen.',
        acceptAll: 'Alle akzeptieren',
        chooseLink: 'Einstellungen',
      },
      step2: {
        title: 'Datenschutzeinstellungen',
        body: 'Beim Besuch dieser Website können Informationen in Ihrem Browser gespeichert oder abgerufen werden, meist in Form von Cookies. Sie können unten einige Arten ablehnen.',
        allowAll: 'Zulassen',
        confirm: 'Meine Auswahl bestätigen',
        errors: { saveFailed: 'Beim Speichern Ihrer Auswahl ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.' },
        categories: {
          necessary: { name: 'Unbedingt erforderlich', desc: 'Notwendig für die Funktion der Website (Warenkorb, Kasse, Sicherheit). Können nicht deaktiviert werden.', alwaysActive: 'Immer aktiv' },
          performance: { name: 'Leistung', desc: 'Ermöglichen die Zählung von Besuchen und Traffic-Quellen zur Messung und Verbesserung der Website-Leistung.' },
          functional: { name: 'Funktional', desc: 'Ermöglichen erweiterte Funktionen und Personalisierung.' },
          targeting: { name: 'Werbung', desc: 'Werden von unseren Werbepartnern genutzt, um relevante Anzeigen auf anderen Websites zu zeigen. Beinhaltet den Verzicht auf Verkauf oder Weitergabe Ihrer personenbezogenen Daten zu Werbezwecken (CCPA).' },
        },
      },
    },
    nl: {
      step1: {
        title: 'Cookies',
        body: 'Door op "Alles accepteren" te klikken, ga je akkoord met het opslaan van cookies op je apparaat om de navigatie te verbeteren, het gebruik van de site te analyseren en onze marketinginspanningen te ondersteunen.',
        acceptAll: 'Alles accepteren',
        chooseLink: 'Voorkeuren',
      },
      step2: {
        title: 'Privacyvoorkeuren',
        body: 'Wanneer je deze site bezoekt, kan deze informatie in je browser opslaan of ophalen, meestal in de vorm van cookies. Je kunt hieronder bepaalde soorten weigeren.',
        allowAll: 'Toestaan',
        confirm: 'Mijn keuzes bevestigen',
        errors: { saveFailed: 'Er is iets misgegaan bij het opslaan van je keuze. Probeer het opnieuw.' },
        categories: {
          necessary: { name: 'Strikt noodzakelijk', desc: 'Vereist voor de werking van de site (winkelwagen, afrekenen, beveiliging). Kunnen niet worden uitgeschakeld.', alwaysActive: 'Altijd actief' },
          performance: { name: 'Prestaties', desc: 'Laten ons bezoeken en verkeersbronnen tellen om de siteprestaties te meten en te verbeteren.' },
          functional: { name: 'Functioneel', desc: 'Maken verbeterde functionaliteit en personalisatie mogelijk.' },
          targeting: { name: 'Advertenties', desc: 'Worden gebruikt door onze advertentiepartners om relevante advertenties op andere sites te tonen. Inclusief het niet verkopen of delen van je persoonlijke gegevens voor advertentiedoeleinden (CCPA).' },
        },
      },
    },
  };

  // ---------------------------------------------------------------------------------------------------------------
  // Consent state — apply/read the visitor's decision via Shopify's Customer Privacy API. Ported unchanged in
  // behavior from cookie-consent-hub/extension/.../consent-state.js (reviewed 2026-09-02, contract confirmed
  // against Shopify's official docs and Hydrogen's first-party TypeScript types).
  // ---------------------------------------------------------------------------------------------------------------
  var DEFAULT_TIMEOUT_MS = 4000;

  function callSetTrackingConsent(consent, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (!api || typeof api.setTrackingConsent !== 'function') {
        reject(new Error('customerPrivacy API unavailable'));
        return;
      }
      var settled = false;
      var timer = setTimeout(function () {
        if (!settled) {
          settled = true;
          reject(new Error('setTrackingConsent timed out'));
        }
      }, timeoutMs);

      api.setTrackingConsent(consent, function (result) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (result && result.error) reject(new Error(result.error));
        else resolve(result);
      });
    });
  }

  function applyConsent(consent, opts) {
    opts = opts || {};
    var maxAttempts = opts.maxAttempts || 3;
    var retryDelayMs = opts.retryDelayMs || 500;
    var timeoutMs = opts.timeoutMs || DEFAULT_TIMEOUT_MS;

    var attempt = 1;
    function tryOnce() {
      return callSetTrackingConsent(consent, timeoutMs)
        .then(function () {
          return { ok: true, attempt: attempt };
        })
        .catch(function (err) {
          if (attempt < maxAttempts) {
            attempt += 1;
            return new Promise(function (resolve) {
              setTimeout(resolve, retryDelayMs * attempt);
            }).then(tryOnce);
          }
          console.error('[cookie-consent] failed to save consent after retries', err);
          return { ok: false, error: err };
        });
    }
    return tryOnce();
  }

  var ALL_GRANTED = { marketing: true, analytics: true, preferences: true, sale_of_data: true };

  function acceptAll(opts) {
    return applyConsent(ALL_GRANTED, opts);
  }

  function allowAll(opts) {
    return applyConsent(ALL_GRANTED, opts);
  }

  function confirmChoices(selections, opts) {
    return applyConsent(
      {
        analytics: !!selections.performance,
        preferences: !!selections.functional,
        marketing: !!selections.targeting,
        sale_of_data: !!selections.targeting,
      },
      opts
    );
  }

  var DECIDED_VALUES = ['yes', 'no'];

  function hasConsentDecision() {
    var api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api || typeof api.currentVisitorConsent !== 'function') return false;
    var current;
    try {
      current = api.currentVisitorConsent();
    } catch (err) {
      console.error('[cookie-consent] currentVisitorConsent() threw', err);
      return false;
    }
    if (!current) return false;
    return ['marketing', 'analytics', 'preferences', 'sale_of_data'].every(function (key) {
      return DECIDED_VALUES.indexOf(current[key]) !== -1;
    });
  }

  // Region gate (the fix this review added — see file header). Fails safe: if the API is missing or throws,
  // we show the banner rather than silently skip it, since under-asking in a regulated region is the worse
  // failure mode of the two.
  function regionRequiresBanner() {
    var api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api || typeof api.shouldShowBanner !== 'function') return true;
    try {
      return !!api.shouldShowBanner();
    } catch (err) {
      console.error('[cookie-consent] shouldShowBanner() threw, showing banner to be safe', err);
      return true;
    }
  }

  // ---------------------------------------------------------------------------------------------------------------
  // UI — ported from banner-ui.js, HTML rebuilt to reuse the theme's own .button/.button--secondary classes
  // (see snippets/cookie-consent.liquid) so colors/border-radius always match Theme settings > Colors/Buttons.
  // ---------------------------------------------------------------------------------------------------------------
  function resolveLocale(requestedLocale) {
    if (!requestedLocale) return 'en';
    if (TRANSLATIONS[requestedLocale]) return requestedLocale;
    var base = requestedLocale.split('-')[0];
    if (TRANSLATIONS[base]) return base;
    return 'en';
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function showConsentError(rootEl, message) {
    var el = rootEl.querySelector('.cch-error');
    if (!el) {
      el = document.createElement('p');
      el.className = 'cch-error';
      el.setAttribute('role', 'alert');
      var container = rootEl.querySelector('.cch-banner, .cch-panel');
      (container || rootEl).appendChild(el);
    }
    el.textContent = message;
  }

  function renderStep1(rootEl, strings, onChooseClick) {
    rootEl.innerHTML =
      '<div class="cch-banner" role="dialog" aria-live="polite">' +
      '  <div class="cch-banner__row">' +
      '    <div>' +
      '      <strong class="cch-banner__title">' + escapeHtml(strings.step1.title) + '</strong>' +
      '      <p class="cch-banner__body">' + escapeHtml(strings.step1.body) + '</p>' +
      '    </div>' +
      '    <div class="cch-banner__actions">' +
      '      <button type="button" class="button button--sm cch-banner__choose-link">' + escapeHtml(strings.step1.chooseLink) + '</button>' +
      '      <button type="button" class="button button--sm cch-banner__accept-btn">' + escapeHtml(strings.step1.acceptAll) + '</button>' +
      '    </div>' +
      '  </div>' +
      '</div>';

    var acceptBtn = rootEl.querySelector('.cch-banner__accept-btn');
    var chooseLink = rootEl.querySelector('.cch-banner__choose-link');
    var errorMessage = (strings.step2 && strings.step2.errors && strings.step2.errors.saveFailed) || 'Something went wrong saving your choice. Please try again.';

    acceptBtn.addEventListener('click', function () {
      acceptBtn.disabled = true;
      chooseLink.disabled = true;
      acceptAll().then(function (result) {
        if (result.ok) {
          rootEl.innerHTML = '';
        } else {
          showConsentError(rootEl, errorMessage);
          acceptBtn.disabled = false;
          chooseLink.disabled = false;
        }
      });
    });

    chooseLink.addEventListener('click', onChooseClick);
  }

  function categoryRow(key, category, forcedChecked) {
    if (key === 'necessary') {
      return (
        '<details class="cch-category">' +
        '  <summary>' + escapeHtml(category.name) + ' <span class="cch-category__always-active">' + escapeHtml(category.alwaysActive) + '</span></summary>' +
        '  <p>' + escapeHtml(category.desc) + '</p>' +
        '</details>'
      );
    }
    return (
      '<details class="cch-category">' +
      '  <summary>' +
      '    <span>' + escapeHtml(category.name) + '</span>' +
      '    <input type="checkbox" class="cch-category__toggle" data-category="' + key + '" ' + (forcedChecked ? 'checked' : '') + ' />' +
      '  </summary>' +
      '  <p>' + escapeHtml(category.desc) + '</p>' +
      '</details>'
    );
  }

  function renderStep2(rootEl, strings, onDone) {
    var cats = strings.step2.categories;
    var errorMessage = (strings.step2.errors && strings.step2.errors.saveFailed) || 'Something went wrong saving your choice. Please try again.';
    rootEl.innerHTML =
      '<div class="cch-panel" role="dialog" aria-live="polite">' +
      '  <strong class="cch-panel__title">' + escapeHtml(strings.step2.title) + '</strong>' +
      '  <p class="cch-panel__body">' + escapeHtml(strings.step2.body) + '</p>' +
      categoryRow('necessary', cats.necessary) +
      categoryRow('performance', cats.performance) +
      categoryRow('functional', cats.functional) +
      categoryRow('targeting', cats.targeting) +
      '  <div class="cch-panel__actions">' +
      '    <button type="button" class="button button--sm cch-panel__confirm">' + escapeHtml(strings.step2.confirm) + '</button>' +
      '    <button type="button" class="button button--sm cch-panel__allow-all">' + escapeHtml(strings.step2.allowAll) + '</button>' +
      '  </div>' +
      '</div>';

    rootEl.querySelectorAll('.cch-category__toggle').forEach(function (input) {
      input.addEventListener('click', function (e) {
        e.stopPropagation();
      });
    });

    var allowAllBtn = rootEl.querySelector('.cch-panel__allow-all');
    var confirmBtn = rootEl.querySelector('.cch-panel__confirm');

    allowAllBtn.addEventListener('click', function () {
      allowAllBtn.disabled = true;
      confirmBtn.disabled = true;
      allowAll().then(function (result) {
        if (result.ok) {
          onDone();
        } else {
          showConsentError(rootEl, errorMessage);
          allowAllBtn.disabled = false;
          confirmBtn.disabled = false;
        }
      });
    });

    confirmBtn.addEventListener('click', function () {
      allowAllBtn.disabled = true;
      confirmBtn.disabled = true;
      var selections = {
        performance: rootEl.querySelector('[data-category="performance"]').checked,
        functional: rootEl.querySelector('[data-category="functional"]').checked,
        targeting: rootEl.querySelector('[data-category="targeting"]').checked,
      };
      confirmChoices(selections).then(function (result) {
        if (result.ok) {
          onDone();
        } else {
          showConsentError(rootEl, errorMessage);
          allowAllBtn.disabled = false;
          confirmBtn.disabled = false;
        }
      });
    });
  }

  function mountBanner(rootEl, locale) {
    if (hasConsentDecision()) return;
    if (!regionRequiresBanner()) return;

    var strings = TRANSLATIONS[resolveLocale(locale)];

    renderStep1(rootEl, strings, function () {
      renderStep2(rootEl, strings, function () {
        rootEl.innerHTML = '';
      });
    });
  }

  // ---------------------------------------------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------------------------------------------
  function afterFeaturesLoaded(root) {
    var locale = document.documentElement.lang || 'en';
    mountBanner(root, locale);
  }

  function init() {
    var root = document.getElementById('cch-root');
    if (!root) return;

    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (error) {
          console.error('[cookie-consent] failed to load Customer Privacy API feature', error);
        }
        afterFeaturesLoaded(root);
      });
    } else {
      afterFeaturesLoaded(root);
    }
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
